import axios, { AxiosError } from 'axios'
import queryString, { Stringifiable } from 'query-string'

import { toastError } from '../components/toasts/Error'
import { config } from '../config/global'
import { KeyedResponseData } from '../global/types'

const handleUnauthorizedRequests = ({ response: { status } }: AxiosError) => {
  const isUnauthorized = status === 401
  if (isUnauthorized) {
    const loginUrl = `${config.loginPortalURL}?redirect_to=${encodeURIComponent(
      window.location.href,
    )}`
    window.location.replace(loginUrl)
  }
  return isUnauthorized
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const loadAsyncLib = import('@deliveryhero/async-jwt-refresh')
  loadAsyncLib.then(component => {
    component.createTokenRefreshAxiosInterceptor(
      axios,
      handleUnauthorizedRequests,
      `${config.iamLoginBaseUrl}auth/refresh_token`,
    )
  })
}

const hasCookie = name =>
  !!document?.cookie?.match(RegExp(`(?:^|;\\s*)${name}=([^;]*)`))

const preProcessOptions = ({ params = {}, data = {}, ...options }) => {
  const withCredentials = config.isDhIamEnabled && hasCookie('dhh_token')
  const token = localStorage.getItem('token')
  const Authorization = `Bearer ${token}`
  const authorizationHeader = withCredentials ? {} : { Authorization }

  const headers = {
    ...(options.skipAuthorizationHeader ? {} : authorizationHeader),
    ...(options.headers || {}),
  }

  const queryOptions = {
    ...options,
  }

  // Avoid leaking our own options into axios.
  delete queryOptions.skipAuthorizationHeader

  return {
    baseURL: options.baseUrl || config.baseUrl,
    params,
    data,
    paramsSerializer: (parameters: Record<string, Stringifiable>) =>
      queryString.stringify(parameters, { arrayFormat: 'bracket' }),
    ...queryOptions,
    headers,
    withCredentials,
  }
}

export async function makeRequest<T>(
  method: string,
  url: string,
  options: Record<string, unknown>,
): Promise<T> {
  const [_statusCode, data] = await makeRequestBase<T>(method, url, options)

  return data
}

/**
 * @returns [statusCode, data]
 */
export async function makeRequestBase<T>(
  method: string,
  url: string,
  options: Record<string, unknown>,
): Promise<[number, T]> {
  try {
    const response = await axios.request(
      preProcessOptions({ method, url, ...options }),
    )

    return [response.status, response.data]
  } catch (error) {
    if (error) {
      toastError(error.message)
    }

    const { data } = error.response

    throw new Error(
      data.message ??
        data.errors?.[0]?.message ??
        data.errors?.[0]?.detail ??
        error.message,
    )
  }
}

const requests = {
  fields: '/fields',
  landingPages: '/landing_pages',
  workflows: '/workflows',
  vehicles: '/vehicles',
  landingPageContents: '/landing_page_contents',
  seos: '/seos',
}

const makeResourceRequests = (
  resourceName,
  items,
): Promise<typeof makeRequestBase>[] => {
  const promises = []
  Object.keys(items).forEach(fieldId => {
    if (items[fieldId].restOperation === 'PATCH') {
      const formatedResource = Object.keys(items[fieldId]).reduce(
        (newObject, currentKey) => {
          // When an nested object has the property restRealKey then this is a FOREIGN key and will be changed when making the actual rest requests
          if (
            typeof items[fieldId][currentKey] === 'object' &&
            items[fieldId][currentKey].restRealKey
          ) {
            return {
              ...newObject,
              [items[fieldId][currentKey].restRealKey]:
                items[fieldId][currentKey].id,
            }
          }

          if (currentKey === 'restOperation') {
            return {
              ...newObject,
            }
          }

          return {
            ...newObject,
            [currentKey]: items[fieldId][currentKey],
          }
        },
        {},
      )
      promises.push(
        makeRequestBase(
          items[fieldId].restOperation,
          `${requests[resourceName]}/${fieldId}`,
          {
            baseUrl: config.dodoBaseApiURL,
            data: formatedResource,
          },
        ),
      )
    }
    if (items[fieldId].restOperation === 'POST') {
      promises.push(
        makeRequestBase(
          items[fieldId].restOperation,
          `${requests[resourceName]}`,
          {
            baseUrl: config.dodoBaseApiURL,
            data: { ...items[fieldId], restOperation: undefined },
          },
        ),
      )
    }
  })

  return promises
}

export function persistChangesInServer(changes: KeyedResponseData) {
  return Promise.all(
    Object.keys(changes)
      .map(resourceName =>
        makeResourceRequests(resourceName, changes[resourceName]),
      )
      .flat(),
  )
}

export function uploadImageInServer(options) {
  const formData = new FormData()
  Object.keys(options).forEach(key => formData.append(key, options[key]))
  return makeRequest('post', 'files', {
    baseUrl: config.dodoBaseApiURL,
    data: formData,
  })
}
